import React, { Fragment, useCallback, useEffect, useMemo } from "react";
import { get } from '@rails/request.js'
import { endOfWeek, startOfWeek } from "./calendar/utils/dates";
import { minutesToMs, standardClassNames } from "./calendar/utils";
import { ClassNameFunction, ItemData, SlotKey } from "./calendar/utils/types";
import { generateSlots } from "./calendar/Week";
import { useImmer } from "use-immer";
import { DayDateLabel, HourLabel } from "./calendar/components/Labels";
import { SlotType } from "./calendar/interactivity/types";
import Dialog, { useDialog } from "./Dialog";
import { Empty } from "../utils";
import { useItems } from "./calendar/utils/items";
import { EventData, RawEventData } from "./availiability/types";
import { GridSlot } from "./calendar/components/GridSlot";
import TurboFrame from "./TurboFrame";

type TimeslotData = {
  id: number;
  type: string;
  link?: string;
  name: string;
  start_time: string; // ISO 8601 string
  end_time: string;   // ISO 8601 string
  events: {
    resource: {
      id: number;
      name: string;
    },
    activity: {
      id: number;
      name: string;
    }
  }
}

function toItemData(data: TimeslotData[]) : ItemData[] {
  return data.map((it) => {
    const start = new Date(Date.parse(it.start_time));
    const end = new Date(Date.parse(it.end_time));
    return {
      itemId: it.id,
      start,
      duration: (end.getTime() - start.getTime()) / (1000 * 60),
    }
  })
}

export const Booking: React.FC<{
  now?: Date;
  start?: Date;
  end?: Date;
  slotSize?: number;
  availabilitiesData?: string[];
  events?: TimeslotData[];
  children?: React.ReactNode;
}> = ({
  now = new Date(),
  start = startOfWeek(now),
  end = endOfWeek(start),
  slotSize = 60,
  availabilitiesData = [],
  events = [],
  children,
}) => {
  const loc = useMemo(() => new URL(window.location.href), []);
  const [ type, setType ] = useImmer<SlotType | null>(null);
  const [ key, setKey ] = useImmer<SlotKey | null>(null);

  const [ dialogOpen, setDialogOpen ] = useImmer(
    loc.pathname.endsWith('/new') ||
    loc.pathname.match(/\/bookings\/\d+$/) !== null
  );

  const duration = slotSize * 2;

  const availabilities = availabilitiesData?.map(range => {
    const [ start, end ] = range.split('..').map(date => new Date(date))
    return { start, end }
  }) || []

  const isAvailable = (slotKey: number) => {
    return Math.max(
      ...availabilities.map(({start, end}) => {
        // If the slot key is in an availability range,
        if(start.getTime() <= slotKey && (slotKey + minutesToMs(duration)) <= end.getTime()) {
          // AND it's not in the past
          if(slotKey >= (new Date()).getTime()) {
            // Return 1
            return 1
          } else {
            // If it's in the past, 0
            return 0
          }
        } else { 
          // If it was never available, -1
          return -1
        }
      })
    )
  }

  // TODO Also middle-click doesn't work :P
  // TODO Find a better date library
  // TODO Find a better URL manipulation library
  const onSelect = useCallback((start: number) => {
    const currentUrl = new URL(window.location.href);
    const newUrl = new URL('bookings/new', window.location.href)

    // Copy existing search parameters
    currentUrl.searchParams.forEach((value, key) => {
      newUrl.searchParams.set(key, value);
    });

    newUrl.searchParams.set(`timeslot[start_time]`, `${start/1000}`);
    newUrl.searchParams.set(`timeslot[end_time]`, `${(start/1000 + duration*60)}`);
    get(newUrl.toString(), { responseKind: 'turbo-stream' }).then(() => setDialogOpen(true));
  }, [slotSize]);

  const onClose = useCallback(() => {
    const currentUrl = new URL(window.location.href);
    const newUrl = new URL('../bookings', window.location.toString());

    // Copy existing search parameters
    currentUrl.searchParams.forEach((value, key) => {
      newUrl.searchParams.set(key, value);
    });

    newUrl.searchParams.delete('timeslot[start_time]');
    newUrl.searchParams.delete('timeslot[end_time]');
    history.pushState({}, '', newUrl.toString());
    setDialogOpen(false);
  }, []);

  // Note: Double back is necessary, because we go an extra forward
  // Note: This somewhat breaks *foward* navigation, but... w/e
  const [doubleBack, setDoubleBack] = useImmer(false);
  window.onpopstate = (event : PopStateEvent) => { 
    if(window.location.pathname.endsWith('/new')) {
      if(doubleBack) {
        history.back();
      } else {
        setDoubleBack(true);
        get(window.location, { responseKind: 'turbo-stream' }).then(() => setDialogOpen(true));
      }
    } else {
      setDoubleBack(false);
      setDialogOpen(false);
    }
  }
  
  const callbacks = (slotKey: number, type : SlotType = "slot") => ({
    onMouseEnter: () => {
      if(isAvailable(slotKey) <= 0) return;
      setType(type);
      setKey(slotKey)
    },
    onMouseLeave: () => {
      setType(null);
      setKey(null)
    },
    onMouseDown: (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.buttons === 1 && isAvailable(slotKey) > 0) {
        onSelect(slotKey);
      }
    },
  });
  
  const isDayHovered = (slotKey: number) => {
    if (!key || type === "time") return false;

    const slotKeyDate = new Date(slotKey);
    const hoveringDate = new Date(key);

    return slotKeyDate.getDay() === hoveringDate.getDay();
  }

  const isTimeHovered = (slotKey: number) => {
    if (!key || type === "day") return false;
    
    const slotKeyDate = new Date(slotKey);
    const hoveringDate = new Date(key);
    return (
      slotKeyDate.getMinutes() === hoveringDate.getMinutes() &&
      slotKeyDate.getHours() === hoveringDate.getHours()
    )
  }

  const isHovered = (slotKey: number, slotType?: SlotType) => {
    if(slotType === "day") return isDayHovered(slotKey);
    if(slotType === "time") return isTimeHovered(slotKey);
    return isDayHovered(slotKey) || isTimeHovered(slotKey);
  }

  const isSelected = (slotKey: number, slotType?: SlotType) => {
    if(!key || type !== "slot") return false;
    return key <= slotKey && slotKey < (key + minutesToMs(duration));
  }

  const slotClassName : ClassNameFunction = ({isHovered, isGutter, isSelected, slotKey} = {}) => 
    `w-full h-full \
    ${slotKey && isAvailable(slotKey) === -1 && !isSelected && ''} \
    ${slotKey && isAvailable(slotKey) === 0 && !isSelected && 'border border-gray-200 bg-gray-100'} \
    ${slotKey && isAvailable(slotKey) === 1 && !isSelected && 'border border-gray-200'} \
    ${isHovered && !isSelected && 'bg-gray-100'} \
    ${isGutter && 'border border-gray-200 text-center p-1'} \
    ${isSelected && 'bg-blue-200 border-blue-200 nav-cursor'}
    ${!isSelected && 'border-gray-200'}`
  
  const {
    gridSlots,
    dayGroups,
    timeGroups,
    dayGutterSlots,
    timeGutterSlots
  } = useMemo(
    generateSlots(slotSize, start, end),
    [slotSize, start, end]
  );

  const calendarItems = useItems(
    toItemData(events), 
    dayGroups, 
    slotSize, 
    'height', 
    () => `absolute cursor-pointer z-10 pl-1 text-gray-400 w-full h-full border border-gray-300 rounded hover:bg-blue-200 bg-gray-100 overflow-hidden`,
    (itemId) => {

      // TODO: This should be a map :P
      const event = events.find(e => e.id === itemId);

      if (event?.type === "Event::GoogleCalendar") {
        window.open(event.link, '_blank')?.focus();
      } else {
        const currentUrl = new URL(window.location.href);
        const newUrl = new URL(`bookings/${itemId}`, window.location.href)
  
        // Copy existing search parameters
        currentUrl.searchParams.forEach((value, key) => {
          newUrl.searchParams.set(key, value);
        });
  
        get(newUrl.toString(), { responseKind: 'turbo-stream' }).then(() => setDialogOpen(true));
      }
    } 
  );
  
  return (
    <>
      <div className={"h-full select-none"}>
        <div className={`grid grid-cols-${Object.keys(dayGutterSlots).length + 1}`}>
          <div className={standardClassNames.slot({isGutter: true})}>&nbsp;</div>

          {/* Day Gutter */}
          { Object.values(dayGutterSlots).map(({ key, Slot }) => (
            <Slot 
              key={key} 
              {...callbacks(key, "day")} 
              className={slotClassName({isGutter: true, isHovered: isHovered(key, "day")})}
              label={<DayDateLabel time={key} />}
            />
          )) }

          { Object.values(timeGroups).sort().map((timeGroup) => {
            const timeKey = timeGroup[0];

            const GutterSlot = timeGutterSlots[timeKey]?.Slot || (() => <></>);
            const gutterClassName = slotClassName({
              isGutter: true, 
              isHovered: isHovered(timeKey, "time")
            });

            return <Fragment key={timeKey}>
              <GridSlot
                gridKey={timeKey} 
                {...callbacks(timeKey, "time")} 
                className={gutterClassName} 
                label={<HourLabel time={timeKey} />}
              />

              { timeGroup.map((key) => {
                const GridSlot = gridSlots[key]?.Slot || (() => <></>);
                const Items = () => (calendarItems[key] || []).map(({key, itemId, Item}) => { 
                  const event = events.find(e => e.id === itemId);
                  return (<Item 
                    key={itemId}
                    {...callbacks(key)}
                  >
                    <i>{event?.name || "???" }</i>
                  </Item>
                )})

                return <GridSlot key={key} className={slotClassName({slotKey: key, isHovered: isHovered(key), isSelected: isSelected(key)})} {...callbacks(key, "slot")} >
                  <Items />
                </GridSlot>
              })}
            </Fragment>;
          })}

        </div>
      </div>
      <Dialog
        onCancel={onClose}
        CancelButton={Empty}
        ConfirmButton={Empty}
        modalOpen={dialogOpen}
      >
        {children}
      </Dialog>
    </>
  );
}

export default Booking;